import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '@shared/components/button';
import { FieldErrorDirective, FieldHintDirective, FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { ModalFooterDirective, ModalShellComponent } from '@shared/components/modal';
import { SegmentOption, SegmentedControlComponent } from '@shared/components/segmented-control';
import { LONGUEURS_MAXIMALES, NatureServiceConnect } from '../../liste-clients.interface';

export interface DialogTypeConnectData {
  typesExistants: string[];
}

export interface DialogTypeConnectOutput {
  nom: string;
  nature: NatureServiceConnect;
}

// Dialog "dumb" : nom et type d'un nouveau service Tick&Live Connect (le nom deviendra
// l'étiquette {type}:{env}, le type décide de ce qu'on y ajoute)
@Component({
  selector: 'app-dialog-type-connect',
  imports: [
    ModalShellComponent,
    ModalFooterDirective,
    ButtonComponent,
    FormFieldComponent,
    FieldErrorDirective,
    FieldHintDirective,
    InputComponent,
    ReactiveFormsModule,
    SegmentedControlComponent,
  ],
  templateUrl: './dialog-type-connect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogTypeConnectComponent {
  // Bornes de saisie partagées avec le backend : une valeur trop longue serait refusée,
  // et surtout la modification ne pourrait pas être enregistrée dans le journal d'audit
  protected readonly longueurs = LONGUEURS_MAXIMALES;

  // `DialogRef` du CDK (via ModalService) et non `MatDialogRef` : la fermeture se
  // consomme par `ref.closed`, pas par `afterClosed()`
  readonly dialogRef = inject<DialogRef<DialogTypeConnectOutput, DialogTypeConnectComponent>>(DialogRef);
  readonly data = inject<DialogTypeConnectData>(DIALOG_DATA);

  nom = new FormControl<string>('', { nonNullable: true });
  protected readonly nomSaisi = toSignal(this.nom.valueChanges, { initialValue: this.nom.value });

  readonly optionsNature: SegmentOption<NatureServiceConnect>[] = [
    { value: 'partenaire', kind: 'text', label: 'Avec interface ITR' },
    { value: 'application', kind: 'text', label: 'Application client' },
    { value: 'front', kind: 'text', label: 'Front web' },
  ];

  nature = signal<NatureServiceConnect>('partenaire');

  // Le nom devient un préfixe d'étiquette : lettres et chiffres uniquement
  readonly formatInvalide = computed(() => {
    const nom = this.nomSaisi().trim();
    return !!nom && !/^[A-Za-z0-9]+$/.test(nom);
  });

  readonly dejaExistant = computed(() => {
    const nom = this.nomSaisi().trim().toLowerCase();
    return !!nom && this.data.typesExistants.some(type => type.toLowerCase() === nom);
  });

  readonly peutValider = computed(() => !!this.nomSaisi().trim() && !this.formatInvalide() && !this.dejaExistant());

  annuler(): void {
    this.dialogRef.close();
  }

  valider() {
    if (!this.peutValider()) {
      return;
    }

    this.dialogRef.close({ nom: this.nom.value.trim(), nature: this.nature() });
  }
}
